import { useState, useEffect } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { combineImages, uploadImageToS3 } from "../services/imageProcessor";
import AvatarUpload from "./AvatarUpload";
import CompanyLogoUpload from "./CompanyLogoUpload";
import SignaturePreview from "./SignaturePreview";
import PhoneInput from "./PhoneInput";
import ThankYou from "./ThankYou";

const REVATOR_LOGO_RUS =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/revator.png";
const REVATOR_LOGO_ENG =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/revator_en.png";
const ALPHA_LOGO =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/alpha.family.png";
const KLS_LOGO = "https://s3.twcstorage.ru/47f406ba-s3-mailsign/kls.png";
const FRANZ_LOGO = "https://s3.twcstorage.ru/47f406ba-s3-mailsign/franz.png";
const COMBOCHIP_LOGO_RUS =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/chip-rus.png";
const COMBOCHIP_LOGO_ENG =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/chip-en.png";
const COMBOLIFT_LOGO_ENG =
  "https://s3.twcstorage.ru/47f406ba-s3-mailsign/service-cl.png";


const resolveLogoByEmail = (email) => {
  if (!email || typeof email !== "string") return { Русский: "", English: "" };
  const parts = email.split("@");
  if (parts.length !== 2) return { Русский: "", English: "" };
  const domain = parts[1].toLowerCase().trim();

  if (domain === "revator.ru") {
    return { Русский: REVATOR_LOGO_RUS, English: REVATOR_LOGO_ENG };
  }
  if (domain === "alfafamily.dev" || domain === "alpha.family") {
    return { Русский: ALPHA_LOGO, English: ALPHA_LOGO };
  }
  if (domain === "lift-home.ru") {
    return { Русский: FRANZ_LOGO, English: FRANZ_LOGO };
  }
  if (domain === "kls.ooo") {
    return { Русский: KLS_LOGO, English: KLS_LOGO };
  }
  if (domain === "combochip.ru") {
    return { Русский: COMBOCHIP_LOGO_RUS, English: COMBOCHIP_LOGO_ENG };
  }
  if (domain === "service-cl.ru") {
    return { Русский: COMBOLIFT_LOGO_ENG, English: COMBOLIFT_LOGO_ENG };
  }
  return { Русский: "", English: "" };
};

const formatPhoneNumber = (phone, isWorkPhone = false) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  let numbers = cleaned;

  if (numbers.startsWith("7") || numbers.startsWith("8")) {
    numbers = "7" + numbers.slice(1);
  } else if (numbers.startsWith("+7")) {
    numbers = "7" + numbers.slice(2);
  }

  if (numbers.startsWith("7") && numbers.length >= 11) {
    const mainPart = numbers.slice(1, 11);
    const extPart = numbers.length > 11 ? numbers.slice(11) : "";
    const match = mainPart.match(/^(\d{3})(\d{3})(\d{2})(\d{2})$/);

    if (match) {
      let result = `+7 (${match[1]}) ${match[2]}-${match[3]}-${match[4]}`;
      if (isWorkPhone && extPart) result += ` доб. ${extPart}`;
      return result;
    }
  }
  return phone;
};

const SignatureForm = () => {
  const [userData, setUserData] = useLocalStorage("userData", {
    firstName: { Русский: "", English: "" },
    lastName: { Русский: "", English: "" },
    position: { Русский: "", English: "" },
    logo: { Русский: "", English: "" },
    mobilePhone: "",
    workPhoneMain: "",
    workPhoneExt: "",
    companyWebsite: "",
    avatar: "",
    email: "",
    combinedImageUrl: "",
    combinedImage: "",
    combinedImageHeight: "",
  });

  const [lang, setLang] = useState("Русский");
  const [isGenerated, setIsGenerated] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const data = urlParams.get("data");

    if (data && !isDataLoaded) {
      try {
        const decodedData = decodeURIComponent(data);
        const userProfile = JSON.parse(decodedData);

        const email = userProfile.email;
        const autoLogos = resolveLogoByEmail(email); // теперь {Русский, English}
        const formattedMobilePhone = formatPhoneNumber(userProfile.phone || "");

        setUserData((prev) => ({
          ...prev,
          firstName: {
            Русский: userProfile.first_name || prev.firstName.Русский,
            English: prev.firstName.English,
          },
          lastName: {
            Русский: userProfile.last_name || prev.lastName.Русский,
            English: prev.lastName.English,
          },
          email: email || prev.email,
          mobilePhone: formattedMobilePhone || prev.mobilePhone,
          avatar: userProfile.avatar_url || prev.avatar,
          // подставляем оба варианта логотипа (автоподстановка только при загрузке)
          logo: {
            Русский: autoLogos.Русский || prev.logo.Русский,
            English: autoLogos.English || prev.logo.English,
          },
        }));

        setIsDataLoaded(true);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (error) {
        console.error("Error parsing data:", error);
        setIsDataLoaded(true);
      }
    }
  }, [setUserData, isDataLoaded]);

  const handleInputChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLangFieldChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const handleLogoChange = (logoUrl) => {
    setUserData((prev) => ({
      ...prev,
      logo: { ...prev.logo, [lang]: logoUrl },
    }));
    handleInputChange("combinedImageUrl", "");
    setIsGenerated(false);
  };

  const handleSave = () => {
    try {
      localStorage.setItem("userData", JSON.stringify(userData));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
      alert("Ошибка при сохранении данных. Проверьте размер данных.");
    }
  };

  const handleClearStorage = () => {
    if (
      window.confirm("Вы уверены, что хотите очистить все сохраненные данные?")
    ) {
      localStorage.removeItem("userData");
      setUserData({
        firstName: { Русский: "", English: "" },
        lastName: { Русский: "", English: "" },
        position: { Русский: "", English: "" },
        logo: { Русский: "", English: "" },
        mobilePhone: "",
        workPhoneMain: "",
        workPhoneExt: "",
        companyWebsite: "",
        avatar: "",
        email: "",
        combinedImageUrl: "",
        combinedImage: "",
        combinedImageHeight: "",
      });
      setIsGenerated(false);
      setIsDataLoaded(false);
    }
  };

  const generateSignature = async () => {
    const logoForLang = userData.logo[lang];
    if (userData.avatar || logoForLang) {
      try {
        setIsGenerating(true);

        const { combinedImage, height } = await combineImages(
          userData.avatar,
          logoForLang
        );
        const combinedImageUrl = await uploadImageToS3(combinedImage);

        setUserData((prev) => ({
          ...prev,
          combinedImageUrl,
          combinedImage,
          combinedImageHeight: height,
        }));

        setIsGenerated(true);
      } catch (error) {
        console.error("Error generating combined image:", error);
        alert("Ошибка при создании комбинированного изображения");
      } finally {
        setIsGenerating(false);
      }
    } else {
      setIsGenerated(true);
    }
  };

  return (
    <div className="signature-form">
      <div className="form-container">
        {/* Язык */}
        <div className="form-row">
          <div className="form-column">
            <div className="input-group">
              <div className="language-control">
                <label className="control-group-label">Язык подписи</label>
                <div className="language-group">
                  {["Русский", "English"].map((l) => (
                    <label key={l} className="language-label">
                      <input
                        type="radio"
                        name="lang"
                        value={l}
                        checked={lang === l}
                        onChange={(e) => setLang(e.target.value)}
                        className="language-input"
                      />
                      <span className="language-custom"></span>
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Аватар и логотип */}
        <div className="form-row">
          <div className="form-column">
            <AvatarUpload
              onImageChange={(image) => {
                handleInputChange("avatar", image);
                handleInputChange("combinedImageUrl", "");
                setIsGenerated(false);
              }}
              currentImage={userData.avatar}
            />
          </div>
          <div className="form-column">
            <CompanyLogoUpload
              onLogoChange={handleLogoChange}
              currentLogo={userData.logo[lang]}
            />
          </div>
        </div>

        {/* Имя, фамилия, должность */}
        <div className="form-row">
          <div className="form-column">
            <div className="input-group">
              <label>Имя</label>
              <input
                type="text"
                value={userData.firstName[lang]}
                onChange={(e) =>
                  handleLangFieldChange("firstName", e.target.value)
                }
              />
            </div>
          </div>
          <div className="form-column">
            <div className="input-group">
              <label>Фамилия</label>
              <input
                type="text"
                value={userData.lastName[lang]}
                onChange={(e) =>
                  handleLangFieldChange("lastName", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column full-width">
            <div className="input-group">
              <label>Должность</label>
              <input
                type="text"
                value={userData.position[lang]}
                onChange={(e) =>
                  handleLangFieldChange("position", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Контакты */}
        <div className="form-row">
          <div className="form-column">
            <div className="input-group">
              <label>Мобильный телефон</label>
              <PhoneInput
                value={userData.mobilePhone}
                onChange={(v) => handleInputChange("mobilePhone", v)}
                isMobile
              />
            </div>
          </div>
          <div className="form-column">
            <div className="input-group">
              <label>Рабочий телефон</label>
              <PhoneInput
                value={{
                  main: userData.workPhoneMain,
                  ext: userData.workPhoneExt,
                }}
                onChange={(main, ext) => {
                  handleInputChange("workPhoneMain", main);
                  handleInputChange("workPhoneExt", ext);
                }}
                isWork
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                value={userData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </div>
          </div>
          <div className="form-column">
            <div className="input-group">
              <label>Сайт компании</label>
              <input
                type="url"
                value={userData.companyWebsite}
                onChange={(e) =>
                  handleInputChange("companyWebsite", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Кнопки */}
        <div className="form-actions">
          <button onClick={handleClearStorage} className="clear-btn">
            Очистить данные
          </button>
          <button
            onClick={() => {
              handleSave();
              generateSignature();
            }}
            className="generate-btn"
            disabled={isGenerating}
          >
            {isGenerating ? "Генерация..." : "Сгенерировать подпись"}
          </button>
        </div>
      </div>

      {isGenerated && <SignaturePreview userData={userData} lang={lang} />}
      {isGenerated && <ThankYou />}
    </div>
  );
};

export default SignatureForm;
